import { useEffect, useRef, useState } from "react";
import { X, Send, Sparkles } from "lucide-react";
import { getWhatsAppUrl, openWhatsApp } from "../lib/whatsapp";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTERS = [
  "¿Qué recomiendan este fin de semana?",
  "Me interesan los servicios de concierge",
  "¿Qué eventos hay en Avándaro?",
  "Una experiencia para una pareja",
];

const WELCOME = "¿En qué podemos ayudarle? Puedo recomendarle una experiencia, contarle qué hay esta semana en el Valle, o conectarlo con el servicio de concierge.";
const BANNER_DISMISS_KEY = "insider_chat_banner_dismissed";

const getSessionId = () => {
  if (typeof window === "undefined") return Math.random().toString(36).slice(2, 10);
  let id = sessionStorage.getItem("insider_chat_session");
  if (!id) {
    id = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("insider_chat_session", id);
  }
  return id;
};

// Parse [[cta:Title|type]] markers out of a stream of text.
// Returns an array of nodes: {kind:'text', value:string} or {kind:'cta', title, ctype}
const parseMessageParts = (text) => {
  if (!text) return [];
  const parts = [];
  const re = /\[\[cta:([^|\]]+)\|(experience|event)\]\]/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push({ kind: "text", value: text.slice(last, m.index) });
    parts.push({ kind: "cta", title: m[1].trim(), ctype: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts;
};

const renderInline = (text) => {
  // Minimal markdown: **bold** + preserve newlines
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} className="text-cobalt-deep">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
};

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: WELCOME },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [waUrl, setWaUrl] = useState("https://wa.me/message/G3MV5LB75ENQG1");
  const [showBanner, setShowBanner] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    getWhatsAppUrl().then(setWaUrl).catch(() => {});
  }, []);

  // Show the invitation banner shortly after mount, unless user dismissed it before.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(BANNER_DISMISS_KEY)) return;
    const t = setTimeout(() => setShowBanner(true), 2200);
    return () => clearTimeout(t);
  }, []);

  const dismissBanner = (e) => {
    if (e) e.stopPropagation();
    setShowBanner(false);
    try { localStorage.setItem(BANNER_DISMISS_KEY, "1"); } catch {}
  };

  const openPanel = () => {
    setOpen(true);
    dismissBanner();
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const handleCta = (title, ctype) => {
    const kind = ctype === "event" ? "evento" : "experiencia";
    openWhatsApp(`Hola. Me interesa la ${kind} "${title}" del Insider. ¿Me confirman disponibilidad?`);
  };

  const send = async (text) => {
    const msg = (text || "").trim();
    if (!msg || streaming) return;
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setInput("");
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const resp = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("Network error");

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop();
        for (const chunk of chunks) {
          if (chunk.startsWith("event: done")) continue;
          if (!chunk.startsWith("data:")) continue;
          const raw = chunk.slice(5).replace(/^ /, "");
          if (!raw || raw === "{}") continue;
          let delta = "";
          try {
            const parsed = JSON.parse(raw);
            delta = parsed.t || "";
          } catch {
            // backwards-compat with plain text SSE
            delta = raw;
          }
          if (!delta) continue;
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", text: (copy[copy.length - 1]?.text || "") + delta };
            return copy;
          });
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessages((m) => {
          const copy = [...m];
          const last = copy[copy.length - 1];
          if (last?.role === "assistant" && !last.text) {
            copy[copy.length - 1] = {
              role: "assistant",
              text: "No pude conectar en este momento. Para asistencia inmediata, escríbanos por WhatsApp.",
            };
          }
          return copy;
        });
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  return (
    <>
      {/* Floating opener */}
      {!open && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3" data-no-translate="true">
          {showBanner && (
            <div
              onClick={openPanel}
              className="chatbot-banner cursor-pointer bg-white border border-cobalt/30 shadow-lg pr-3 pl-4 py-2.5 flex items-center gap-3 group"
              data-testid="chatbot-banner"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openPanel()}
            >
              <Sparkles size={14} className="text-cobalt shrink-0" />
              <div className="min-w-0">
                <p className="font-display text-[0.55rem] tracking-[0.3em] uppercase text-cobalt font-semibold leading-none">Pregunte al Insider</p>
                <p className="font-serif text-[0.95rem] text-ink leading-tight mt-1">¿Qué le recomendamos esta semana? <span className="serif-italic text-cobalt">Pregúntenos.</span></p>
              </div>
              <button
                onClick={dismissBanner}
                className="ml-1 text-subtle hover:text-ink shrink-0"
                aria-label="Cerrar invitación"
                data-testid="chatbot-banner-dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <button
            onClick={openPanel}
            className="w-14 h-14 rounded-full bg-cobalt-deep text-white shadow-xl hover:scale-105 transition-transform flex items-center justify-center font-serif text-[1.6rem] leading-none"
            data-testid="chatbot-opener"
            aria-label="Abrir asistente"
          >
            <span className="serif-italic -translate-y-[1px]">v</span>
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-50 w-[min(400px,calc(100vw-32px))] h-[min(620px,calc(100vh-64px))] bg-white border border-cobalt/30 shadow-2xl flex flex-col"
          data-testid="chatbot-panel"
          data-no-translate="true"
        >
          {/* Header */}
          <div className="bg-cobalt-deep text-white px-5 py-4 flex items-start justify-between">
            <div>
              <p className="font-display text-[0.55rem] tracking-[0.3em] uppercase opacity-80">Asistente · Insider</p>
              <p className="font-serif text-xl mt-1 leading-none">
                Valle de Bravo <span className="serif-italic">Insider</span>
              </p>
            </div>
            <button onClick={() => setOpen(false)} className="opacity-80 hover:opacity-100 shrink-0" aria-label="Cerrar" data-testid="chatbot-close">
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="chatbot-messages">
            {messages.map((m, i) => {
              const parts = m.role === "assistant" ? parseMessageParts(m.text) : null;
              return (
                <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                  <div
                    data-role={m.role}
                    className={
                      m.role === "user"
                        ? "max-w-[85%] bg-sand text-ink text-sm leading-relaxed px-4 py-3 whitespace-pre-line"
                        : "max-w-[90%] text-ink text-sm leading-relaxed font-serif space-y-2"
                    }
                  >
                    {m.role === "user" ? (
                      m.text
                    ) : parts && parts.length > 0 ? (
                      parts.map((p, j) =>
                        p.kind === "text" ? (
                          <div key={j} className="whitespace-pre-line">{renderInline(p.value)}</div>
                        ) : (
                          <button
                            key={j}
                            onClick={() => handleCta(p.title, p.ctype)}
                            className="inline-flex items-center gap-2 bg-cobalt-deep text-white px-4 py-2 font-display text-[0.65rem] tracking-[0.22em] uppercase font-semibold hover:bg-cobalt transition-colors not-italic"
                            data-testid={`chat-cta-${p.ctype}-${p.title.slice(0, 12)}`}
                          >
                            Reservar «{p.title}» por WhatsApp →
                          </button>
                        )
                      )
                    ) : streaming && i === messages.length - 1 ? (
                      <span className="text-subtle text-xs">pensando…</span>
                    ) : null}
                  </div>
                </div>
              );
            })}

            {/* Starter prompts (only on empty conversation) */}
            {messages.length === 1 && !streaming && (
              <div className="pt-2 space-y-2" data-testid="chatbot-starters">
                {STARTERS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="w-full text-left text-[0.8rem] text-cobalt border border-cobalt/40 px-3 py-2 hover:bg-cobalt hover:text-white transition-colors"
                    data-testid={`chatbot-starter-${s.slice(0, 10)}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* WhatsApp fallback bar */}
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-sand border-t border-cobalt/20 px-5 py-2 text-[0.68rem] tracking-[0.22em] uppercase text-cobalt text-center hover:bg-cream font-display font-semibold"
            data-testid="chatbot-whatsapp"
          >
            ¿Prefiere escribir? Hablar por WhatsApp →
          </a>

          {/* Input */}
          <div className="border-t border-border2 p-3 flex gap-2 items-end bg-white">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Escriba su pregunta…"
              rows={1}
              disabled={streaming}
              className="flex-1 bg-transparent border border-border2 px-3 py-2 text-sm focus:border-cobalt resize-none max-h-24 min-h-[40px]"
              data-testid="chatbot-input"
            />
            <button
              onClick={() => send(input)}
              disabled={streaming || !input.trim()}
              className="bg-cobalt-deep text-white px-3 py-2 disabled:opacity-40 shrink-0 h-10 inline-flex items-center justify-center"
              data-testid="chatbot-send"
              aria-label="Enviar"
            >
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
